/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useMsal } from "@azure/msal-react"
import { Typography } from "@mui/material"
import { cloneDeep, List } from "lodash"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-hot-toast"
import { usePostRequest } from "../../../../../../../api/helper"
import { config } from "../../../../../../../AppConfig/config"
import { loginRequest, requestIdToken } from "../../../../../../../auth/authConfig"
import { tableHeaderGray } from "../../../../../../../constants/colors"
import VAR_COMMON_CONSTANTS from "../../../../../../../constants/globalVariables"
import { theme } from "../../../../../../../constants/theme"
import { ETrackedItemTypes } from "../../../../../../../constants/trackedItems"
import { ISetRecentlyViewedParams, useSetRecentlyViewedWithDeps } from "../../../../../../../hooks/useSetRecentlyViewedWithDeps"
import { useHasWriteAccess } from "../../../../../../../redux/ducks/AccessControl/accessControlHelpers"
import { selectCurrentUser } from "../../../../../../../redux/ducks/AccessControl/slice"
import {
    editDatasetInExecutionStatusMap,
    editMergeSetLocalState,
    selectBenchmarkData,
    selectCoverageRequirementsDataState,
    selectDatasetsExecutionStatus,
    selectTriggerFetchDatasetData,
    setCoverageRequirementsData,
    setIsCoverageRequirementsSaved
} from "../../../../../../../redux/ducks/CoverageRequirements/slice"
import { useAppDispatch, useAppSelector } from "../../../../../../../redux/hooks"
import ArgosCollapseComponent from "../../../../../../components/CollapseComponent/ArgosCollapseComponent"
import ArgosFlexbox from "../../../../../../components/Containers/ArgosFlexbox"
import BoxWithCenteredContent from "../../../../../../components/Containers/BoxWithCenteredContent"
import Loader from "../../../../../../components/Loader/Loader"
import useGetHubName from "../../../../../../components/Socket/useGetHubName"
import useSocket from "../../../../../../components/Socket/useSocket"
import {
    EDatasetExecutionStatus,
    IN_PROGRESS_STATUSES,
    KEY_NAME_DRIVING_SCENARIO_DESIGN_EXPERIMENTS,
    KEY_NAME_DRIVING_SCENARIO_PROPERTIES,
    KEY_NAME_REDLINE_TYPE,
    KEY_NAME_SOURCE_DATASET,
    MERGED_DATASET_TYPE_STR,
    SINGLE_DATASET_TYPE_STR
} from "../../../constants"
import { getCoverageRequirementsSelectedDatasetId } from "../CoverageRequirements"
import { AnalysisResults } from "./AnalysisResults"
import {datasetSaveData, downloadDatasetAsCSV, fetchDatasetDataEndpoint, OBJECT_TYPE_STR, PACKAGE_TYPE_STR, requiredPropertiesDefault} from "./constants"
import DatasetControlPanel from "./DatasetControlPanel"
import { DrivingScenarioDesignExperiments } from "./DrivingScenarioDesignExperiments"
import { DrivingScenarioProperties } from "./DrivingScenarioProperties"
import ExecutionErrorsVisualizer from "./ExecutionErrorsVisualizer"
import MergeStatus from "./MergeStatus"
import SourceDatasets from "./SourceDatasets"

import { v4 as uuidv4 } from 'uuid'
import { defaultXLabel, defaultYLabel } from "./AnalysisResultTab/constants"

export interface PropertiesData {
  type: string
  category: string
  name: string
  property_link: string
}

// make interface ObjectPropertiesData that is same as PropertiesData but with additional objectId and variantName
export interface ObjectPropertiesData extends PropertiesData {
  objectId: string
  variantName: string
  metaData?: any // additional metadata for the object, like adasProgramId
}

export interface ProcessedPropertiesData {
  type: string
  name: string
  subRows: any
}

interface RightPanelCoverageRequirementsProps {
  datasetPath: string[]
  studyId: string
  onClickAbort: any
  datasetChange: any
  setDatasetChange: any
  fetchStudy: any
  isReadOnlyStudy?: boolean
}

const RightPanelCoverageRequirements = (props: RightPanelCoverageRequirementsProps) => {
  const isReadOnlyStudy: boolean | undefined = props?.isReadOnlyStudy
  const dispatch = useAppDispatch()
  const datasetId = getCoverageRequirementsSelectedDatasetId()
  const [componentsDataMap, setComponentsDataMap] = useState<any>(null)
  const hasWriteAccess = useHasWriteAccess()
  const VAR_DATA_SETS = "Raw  " + VAR_COMMON_CONSTANTS.VAR_DATA_SETS
  const drivingScenarioPropertiesTitle = hasWriteAccess ? "Configure Driving Scenario Properties" : "Driving Scenario Properties"
  const drivingScenarioExperimentsTitle = hasWriteAccess ? "Select Driving Scenario and Set up Design of Experiments" : "Driving Scenario and Set up Design of Experiments"
  const selectedDatasetExecutionStatus = useAppSelector((state) => selectDatasetsExecutionStatus(state)?.[datasetId])
  const {instance, accounts} = useMsal()

  /**
   * Load dataset data and create component
   */
  const [isLoadingDatasetData, setIsLoadingDatasetData] = useState<boolean>(false)
  const triggerFetchDatasetsData = useAppSelector(selectTriggerFetchDatasetData)
  const isLogModeEnabled = useAppSelector((state) => selectBenchmarkData(state).isLogModeEnabled)
  const benchmarkData = useAppSelector(selectBenchmarkData)
  const [rightPanelErrLabel,setRightPanelErrLabel] = useState<string>()
  const generalRightPanelErrMsg = "Data failed to load please contact support."
  const gmid = useAppSelector(state => selectCurrentUser(state).id)

  /**
   * Handles fetching of dataset data when the datasetId changes or the triggerFetchDatasetsData changes.
   */
  useEffect(() => {
    const abortController = fetchDataForDataset(false, true)
    return () => {
      if (abortController) {
        abortController.abort()
      }
    }
  }, [datasetId, triggerFetchDatasetsData, gmid])
  const [isFetchingRaw, setIsFetchingRaw] = useState(false)
  const [errorFetchingRaw, setErrorFetchingRaw] = useState(false)
  const setLoadingState = (isLoading: boolean, fetchRaw: boolean, showMainLoader: boolean) => {
    if (fetchRaw) {
      setIsFetchingRaw(isLoading)
    }
    if (showMainLoader) {
      setIsLoadingDatasetData(isLoading)
    }
    if (!isLoading) {
      setIsFetchingRaw(false)
      setIsLoadingDatasetData(false)
    }
  }

  const [loadRawDatasetsData, setLoadRawDatasetsData] = useState<any>(null)

  const currentAbortControllerRef = useRef<AbortController | null>(null);

  /**
   * Fetches data for a dataset based on the provided parameters.
   * 
   * @param fetchRaw - True if fetching raw data, otherwise only fetch redline.
   * @param showMainLoader - True if showing Loader over the entire right panel page.
   * @param doeValues - Optional list of doe column data to show in graph tooltip when hovering over a point.
   * @param x_axis - Selected x-axis column name.
   * @param y_axis - Selected y-axis column name.
   * @param objectIds - Data will be filtered based on list of object ids.
   * 
   * @returns An AbortController instance if a request is made, otherwise null.
   */
  const fetchDataForDataset = (fetchRaw: boolean, showMainLoader: boolean, 
    doeValues: List<String> = [], x_axis: string = defaultXLabel.variable_name, 
    y_axis: string = defaultYLabel.variable_name, objectIds: List<String> = []): AbortController | null => {
    if (datasetId != undefined && datasetId?.length > 0) {
      // Abort the previous request if it exists
      if (currentAbortControllerRef.current) {
        currentAbortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      currentAbortControllerRef.current = abortController;

      setLoadingState(true, fetchRaw, showMainLoader);
      setErrorFetchingRaw(false);

      const loadOfRawDatasetsId = uuidv4();
      setLoadRawDatasetsData({loadOfRawDatasetsId});
      let body: any = {
        "selectedDatasetId": datasetId, fetchRaw
      };
      if (x_axis.length > 0 && y_axis.length > 0) {
        body["x_axis"] = x_axis;
        body["y_axis"] = y_axis;
      }
      if (fetchRaw) {
        body["loadOfRawDatasetsId"] = loadOfRawDatasetsId;
      }
      if (doeValues.length > 0) {
        body["doeValues"] = doeValues
      }
      if (objectIds.length > 0) {
        body["mergedObjectsToPlot"] = objectIds
      }
      if (isLogModeEnabled) {
        body["logData"] = cloneDeep(benchmarkData);
        body["logData"]["frontEndTimestamp"] = (new Date()).getTime().toString();
      }

      const url = `${config.url.API_URL}${fetchDatasetDataEndpoint}`;
      if (showMainLoader) {
        dispatch(setCoverageRequirementsData(null));
      }

      requestIdToken(instance, accounts).then((idTok) => {
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            "Authorization": `Bearer ${idTok}`
          },
          body: JSON.stringify(body),
          signal: abortController.signal
        }).then((response) => {
          if (response.status === 401) {
            instance.loginRedirect(loginRequest).catch(e => {
              console.log(e);
            });
          }
          return response.json();
        }).then((data: any) => {
          if ("error" in data){
            setRightPanelErrLabel(data["error"])
            toast.error("Error loading dataset data");
          }
          const deepCloneOfData = cloneDeep(data);
          
          setLoadingState(false, fetchRaw, showMainLoader);

          setErrorFetchingRaw(false);
          setRecentlyViewed();
          dispatch(setCoverageRequirementsData(deepCloneOfData));
        }).catch((e: any) => {
          if (e.name === "AbortError") {

          } else {
            console.log(e);
            setLoadingState(false, fetchRaw, showMainLoader);
            setErrorFetchingRaw(true);
            setRightPanelErrLabel(generalRightPanelErrMsg);
            toast.error("Error loading dataset data");
            dispatch(setCoverageRequirementsData(null));
          }
        });
      }).catch((e) => {
        console.log(e);
        setRightPanelErrLabel(generalRightPanelErrMsg);
        toast.error("Error loading dataset data");
        setErrorFetchingRaw(true);
      });

      return abortController;
    } else {
      return null;
    }
  };

  const recentlyViewedParams: ISetRecentlyViewedParams = {
    trackedItemUid: datasetId,
    partitionKey: datasetId,
    trackedItemType: ETrackedItemTypes.COVERAGE_REQUIREMENTS_DATASET,
  }
  const setRecentlyViewed = useSetRecentlyViewedWithDeps(recentlyViewedParams, false)

  /**
   * Updates Collapsible component on data change
   */
  const coverageRequirementsData = useAppSelector(selectCoverageRequirementsDataState)
  const datasetOwner = coverageRequirementsData?.owner
  const datasetName = coverageRequirementsData?.name
  const datasetType = coverageRequirementsData?.["redlineType"]
  useEffect(() => {
    // if (coverageRequirementsData == null || datasetId != coverageRequirementsData.id) return
    // if (coverageRequirementsData == null) return
    createCollapsibleTabs(coverageRequirementsData)
  }, [isReadOnlyStudy, coverageRequirementsData, isLoadingDatasetData, selectedDatasetExecutionStatus, isFetchingRaw, errorFetchingRaw, datasetId])

  const createCollapsibleTabs = (_data: any) => {
    if (_data == null) {
      setComponentsDataMap(null)
      return
    }
    const data: any = cloneDeep(_data)
    const isLoadingHere = isLoadingDatasetData || datasetId != _data.id

    const doeTableData = data?.data?.["doe_table"]

    if (data[KEY_NAME_REDLINE_TYPE] == SINGLE_DATASET_TYPE_STR) {
      const propertiesData: PropertiesData[] = data.data[KEY_NAME_DRIVING_SCENARIO_PROPERTIES]
      const processedData: ProcessedPropertiesData[] = processPropertiesData(propertiesData, SINGLE_DATASET_TYPE_STR);
      let drivingExperimentsData = data.data[KEY_NAME_DRIVING_SCENARIO_DESIGN_EXPERIMENTS]

      if (Object.keys(drivingExperimentsData).length == 0) {
        drivingExperimentsData["variables"] = []
        drivingExperimentsData["generalInfo"] = {}
      }

      const newComponentData: any = {
        [drivingScenarioPropertiesTitle]: <Loader isLoading={isLoadingHere}>
          <DrivingScenarioProperties
            isLoading={isLoadingHere}
            studyId={props.studyId}
            selectedDatasetId={datasetId}
            loadedPropertiesData={processedData}
            saveNewInfo={handlePropertiesDataChange}
            propertiesFlatList={propertiesData}
            datasetType={datasetType}
            isReadOnlyStudy={isReadOnlyStudy}
          />
        </Loader>,
        [drivingScenarioExperimentsTitle]: <Loader isLoading={isLoadingHere}>
          <DrivingScenarioDesignExperiments
            isLoading={isLoadingHere}
            selectedDatasetId={datasetId}
            loadedDrivingExperimentData={drivingExperimentsData}
            handleExperimentsDataChange={handleExperimentsDataChange}
            isReadOnlyStudy={isReadOnlyStudy}
          />
        </Loader>,
        "Results": <AnalysisResults
          isLoading={isLoadingHere}
          resultPlottingData={doeTableData}
          properties={propertiesData}
          datasetType={data[KEY_NAME_REDLINE_TYPE]}
          preselectedObjects={Object.keys(doeTableData)}
          fetchData={(fetchRaw: boolean, showMainLoader: boolean, doeValues?: List<String>, x_axis?: string, y_axis?: string, objectIds?: List<String>) => {
            fetchDataForDataset(fetchRaw, showMainLoader, doeValues ?? [], x_axis, y_axis, objectIds)
          }}
          isLoadingRawData={isFetchingRaw}
          errorFetchingRawData={errorFetchingRaw} 
          loadRawDatasetsData={loadRawDatasetsData}
          allowUsingDoeVarsAsAxes={true}
          />,
      }
      const executionErrors = data?.["errorMessage"]

      if (executionErrors != null && Object.keys(executionErrors).length > 0) {
        newComponentData["Execution Errors"] = <ExecutionErrorsVisualizer errors={executionErrors}/>
      }
      setComponentsDataMap(newComponentData);
    } else if (data[KEY_NAME_REDLINE_TYPE] == MERGED_DATASET_TYPE_STR) {
      const propertiesData: PropertiesData[] = data.data[KEY_NAME_DRIVING_SCENARIO_PROPERTIES]
      const processedData: ProcessedPropertiesData[] = processPropertiesData(propertiesData, MERGED_DATASET_TYPE_STR)
      const objectDataForPlotting: PropertiesData[] = propertiesData

      const newComponentData = {
        "Merged Red Line Properties": <DrivingScenarioProperties
          isLoading={isLoadingDatasetData}
          studyId={props.studyId}
          selectedDatasetId={datasetId}
          loadedPropertiesData={processedData}
          propertiesFlatList={propertiesData}
          datasetType={datasetType}
          isReadOnlyStudy={isReadOnlyStudy}
        />,
        "Merged Red Line Graph": <AnalysisResults
          isLoading={isLoadingDatasetData}
          resultPlottingData={doeTableData}
          properties={objectDataForPlotting}
          datasetType={data[KEY_NAME_REDLINE_TYPE]}
          outOfDate={data['outOfDate']}
          fetchData={(fetchRaw: boolean, showMainLoader: boolean, doeValues?: List<String>, x_axis?: string, y_axis?: string, objectIds?: List<String>) => {
            fetchDataForDataset(fetchRaw, showMainLoader, doeValues ?? [], x_axis, y_axis, objectIds)
          }}
          isLoadingRawData={isFetchingRaw}
          errorFetchingRawData={errorFetchingRaw}
          loadRawDatasetsData={loadRawDatasetsData} 
          preselectedObjects={undefined}   
          allowUsingDoeVarsAsAxes={true}
          />,
        [VAR_DATA_SETS]: <SourceDatasets
          studyId={props?.studyId}
          sourceDatasets={_data["source_data_set_info"]}
        />
      }
      setComponentsDataMap(newComponentData)
    } else if (data.type === "mergeOperation") {
      const generationStatus = data.generationStatus
      const newComponentData: any = {
        "Generation Status": <MergeStatus
          mergeSetId={datasetId}
          generationStatus={generationStatus}
        />,
      }
      const executionErrors = data?.errorMessage
      if (executionErrors != null && Object.keys(executionErrors).length > 0) {
        newComponentData["Generation Errors"] = <ExecutionErrorsVisualizer errors={executionErrors}/>
      }
      setComponentsDataMap(newComponentData)
    }
  }


  /**
   * Driving Experiments Data
   */
  const handleExperimentsDataChange = (newData: any) => {
    let deepCopy = cloneDeep(coverageRequirementsData)
    let newDataCopy = cloneDeep(newData)
    deepCopy.data.driving_scenario_design_experiments = newDataCopy
    updateCoverageRequirementsRedux(deepCopy, false)
  }
  /**
   * Driving Scenario Properties Data
   *
   * @param newData - New data to be added to driving_scenario_properties. Could be a single object or an array of objects.
   * @param isAdd - True if adding new data.
   * @param _relativeIndex - Index of the object in the properties list to be edited. Relative because this is index within a type.
   */
  const handlePropertiesDataChange = (newData: any, isAdd: boolean, _relativeIndex: any) => {
    // TODO: For object save right now, if we only want to save objectId, we need name a category to get in the properties somehow. So we could do that in redux (whenever that state is set, update all object properties).
    // Then we would need rwo data in redux also, and use a method to find the info given variant. We can also do this on backend, problem is that we still need to do it on frontend in this case, so maybe could just do all on fe.
    let currentCoverageRequirementsDeepCopy = cloneDeep(coverageRequirementsData)
    let newDataCopy = cloneDeep(newData)
    if (isAdd) {
      let propertiesList = currentCoverageRequirementsDeepCopy.data.driving_scenario_properties
      let newPropertiesToAdd: any = []
      if (!Array.isArray(newDataCopy)) {
        // tree node selector returns as an object, rest of property types are array of objects
        newDataCopy = [newDataCopy]
      }
      // Add the object to the list if it is not already in the list
      newDataCopy.forEach((element: any) => {
        if (element.type === OBJECT_TYPE_STR) {
          if (!propertiesList.some((property: any) => (property.objectId === element.objectId))) {
            newPropertiesToAdd.push(element)
          }
        } else if (element.type === PACKAGE_TYPE_STR) {
          if (!propertiesList.some((property: any) => (property?.metaData?.adasProgramId != null && (property?.metaData?.adasProgramId === element?.metaData?.adasProgramId)))) {
            newPropertiesToAdd.push(element)
          }
        } else if (!propertiesList.some((property: any) => ((property.type === element.type) && (property.name === element.name)))) {
          newPropertiesToAdd.push(element)
        }
      })
      let newPropertiesList = currentCoverageRequirementsDeepCopy.data.driving_scenario_properties.concat(newPropertiesToAdd)
      currentCoverageRequirementsDeepCopy.data.driving_scenario_properties = newPropertiesList
    } else if (!isAdd && _relativeIndex != undefined) {
      const globalIndex = getDrivingScenarioPropertyGlobalIndexFromRelativeIndex(currentCoverageRequirementsDeepCopy, _relativeIndex, newDataCopy.type)
      // first check if globalIndex is a valid index for the list
      if (globalIndex >= currentCoverageRequirementsDeepCopy.data.driving_scenario_properties.length || globalIndex < 0) {
        toast.error("Error")
        return
      }
      currentCoverageRequirementsDeepCopy.data.driving_scenario_properties[globalIndex] = newDataCopy
    } else {
      currentCoverageRequirementsDeepCopy.data.driving_scenario_properties = newData
    }
    updateCoverageRequirementsRedux(currentCoverageRequirementsDeepCopy, false)
  }

  /**
   * Helper function to set the redux state
   * @param _data
   * @param backendSave
   */
  const updateCoverageRequirementsRedux = (_data: any, backendSave: boolean | null) => {
    // dont overwrite data.doe_table
    if (_data != null && _data.data != null && coverageRequirementsData != null && coverageRequirementsData.data != null) {
      _data.data["doe_table"] = coverageRequirementsData.data["doe_table"]
    }
    dispatch(setCoverageRequirementsData(_data))
    if (backendSave != null) {
      dispatch(setIsCoverageRequirementsSaved(backendSave))
    }
  }

  const fetchSaveDataHandle = usePostRequest(datasetSaveData)
  const saveDataToDatabase = () => {
    const payloadToSend: any = {};
    Object.keys(coverageRequirementsData.data).forEach((key: any) => {
      if (key != "doe_table") {
        payloadToSend[key] = coverageRequirementsData.data[key]
      }
    })
    if (coverageRequirementsData == null) {
      toast.error("Error saving data to database")
      return
    }
    let payload: any = {
      //Workaround until bulk edit fixed then delete
      saveDatasetConfig:true,
      data: payloadToSend,
      datasetId: datasetId,
      studyId: props?.studyId
    }
    if (selectedDatasetExecutionStatus == EDatasetExecutionStatus.COMPLETED) {
      payload["executionStatus"] = EDatasetExecutionStatus.INVALID_RESULTS
    }
    fetchSaveDataHandle(payload).then((_data: any) => {
      updateCoverageRequirementsRedux(_data, true)
      dispatch(editDatasetInExecutionStatusMap({datasetId, executionStatus: _data.executionStatus}))
      props.fetchStudy()
    }).catch((e: any) => {
      toast.error("Error saving data to database")
    })
  }

  /**
   * Download dataset as a csv to user's download folder
   * @param downloadFileName
   */
  const handleDownloadDatasetFile = async (downloadFileName: any, red: boolean, raw: boolean, rawFromDb: boolean = false, rawFromDbWithAnnotations: boolean = false) => {
    let url = `${config.url.API_URL}${downloadDatasetAsCSV}`
    const idTok = await requestIdToken(instance, accounts);
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "Authorization": `Bearer ${idTok}`
      },
      body: JSON.stringify({
        "fileName": downloadFileName,
        "selectedDatasetId": datasetId,
        "red": red,
        "raw": raw,
        "rawFromDb": rawFromDb,
        "rawFromDbWithAnnotations": rawFromDbWithAnnotations
      })
    }).then((data) => {
      if (data.status === 401)
        instance.loginRedirect(loginRequest).catch(e => {
          console.log(e);
        });

      data.blob().then((blob: any) => {
        const fileURL = window.URL.createObjectURL(blob)
        let alink = document.createElement('a');
        alink.href = fileURL;
        alink.download = `${downloadFileName}.csv`;
        alink.click();
      })
    })
      .catch((e) => {
        console.log(e)
        toast.error("An error ocurred when downloading your data")
      })
  }

  /**
   * Socket to receive merge set progress
   */
  const onMergeMessage = useCallback((msg: any) => {
    const {mergeSetId, status, message, percentage, currentIteration, totalIterations} = msg
    dispatch(editMergeSetLocalState({mergeSetId, data: {status, message, percentage, currentIteration, totalIterations}}))
  }, [])
  const mergeHubName = useGetHubName(`mergeProgress_${props.studyId}`)
  useSocket(mergeHubName, onMergeMessage)

  const collapseComponentStyleProps = useMemo(() => {
    return {
      outlineOnly: true,
      fontColor: theme.layout.sidebar.backgroundColor,
    }
  }, [])
  const [collapseComponentOpenList, setCollapseComponentOpenList] = useState<string[]>([])
  // const coverageRequirementsClipboard = useAppSelector(state => selectCoverageRequirementsClipboard(state))
  //
  // const handleKeyDown = (event: any) => {
  //    if(event.keyCode === 27 && coverageRequirementsClipboard !== null){
  //       dispatch(setCoverageRequirementsClipboard(null))
  //       toast.success("Successfully cleared the clipboard!")
  //     }
  //     else if(event.keyCode === 27 && coverageRequirementsClipboard == null){
  //       toast.success("Your clipboard is Empty!")
  //        return
  //     }
  // }
  return (<>
    {datasetId == undefined &&
      <ArgosFlexbox style={{height: '100%', width: '100%', justifyContent: 'center', alignContent: 'center'}}>
        <Typography sx={{color: tableHeaderGray, fontSize: 16, fontWeight: 'light', justifyContent: 'center', alignContent: 'center'}}>
          Select Data Set
        </Typography>
      </ArgosFlexbox>
    }
    {datasetId != undefined &&
      <ArgosFlexbox vertical style={{height: "100%", width: "100%", overflow: "auto"}}>
        <ArgosFlexbox style={{height: 'auto'}}>
          <DatasetControlPanel
            saveToDatabase={saveDataToDatabase}
            datasetId={datasetId}
            studyId={props.studyId}
            datasetType={datasetType}
            datasetName={datasetName}
            datasetOwner={datasetOwner}
            datasetPath={props.datasetPath}
            onClickAbort={props?.onClickAbort}
            // favoriteIcon={props.favoriteIcon}
            datasetChange={props.datasetChange}
            setDatasetChange={props.setDatasetChange}
            executionStatus={selectedDatasetExecutionStatus}
            handleDownloadDatasetFile={handleDownloadDatasetFile}
            isReadOnlyStudy={isReadOnlyStudy}
          />
        </ArgosFlexbox>
        {/*{(componentsDataMap != null && Object.keys(componentsDataMap)?.length > 0) && !isLoadingDatasetData ?*/}
        {(componentsDataMap != null && Object.keys(componentsDataMap)?.length > 0) ?
          <div style={{flexGrow: 1, overflow: "auto"}}>
            {
              <ArgosCollapseComponent
                styleProps={collapseComponentStyleProps}
                data={componentsDataMap}
                defaultOpenKeys={collapseComponentOpenList}
                onClickListItem={(clickedKey: string, isOpening: boolean) => {
                  // console.log("Clicked on item: ", clickedKey, " and it is opening: ", isOpening)
                }}
                setCurrentOpenKeysList={setCollapseComponentOpenList}
              />
            }
          </div>
          :
          <BoxWithCenteredContent vertical horizontal style={{
            flexGrow: 1,
            display: datasetId?.length > 0 ? 'flex' : 'none',
            height: '100%',
            width: '100%'
          }}>
            {isLoadingDatasetData ? <Loader isLoading={true}/> : <p>{rightPanelErrLabel}</p>}
          </BoxWithCenteredContent>
        }
      </ArgosFlexbox>
    }
  </>)
}
export default React.memo(RightPanelCoverageRequirements);

const processPropertiesData = (data: any, datasetType: string) => {
  const listOfRows: any = []
  const newData = data.reduce((acc: any, obj: any) => {
    const key = obj["type"]
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(obj)
    return acc
  }, {})
  if (datasetType === SINGLE_DATASET_TYPE_STR) {
    requiredPropertiesDefault.forEach((propertyType: any) => (
      listOfRows.push({
        "type": propertyType,
        "name": "",
        "subRows": newData?.[propertyType] ?? []
      })
    ))
  } else if (datasetType === MERGED_DATASET_TYPE_STR) {
    Object.keys(newData).forEach(element => {
      const objList = newData[element]
      listOfRows.push({
        "type": element,
        "name": "",
        "subRows": objList
      })
    })
  }
  return listOfRows
}

const getDrivingScenarioPropertyGlobalIndexFromRelativeIndex = (_coverageRequirementsData: any, relativeIndex: any, type: string) => {
  let indexCounter = 0
  let globalIndexToEdit = 0
  for (let i = 0; i < _coverageRequirementsData.data.driving_scenario_properties.length; i++) {
    if (_coverageRequirementsData.data.driving_scenario_properties[i].type == type) {
      if (indexCounter == relativeIndex) {
        // then we found the index we want to edit
        globalIndexToEdit = i
        break
      }
      indexCounter++
    }
  }
  return globalIndexToEdit
}

